import React, { useState } from 'react';
import { isWeb } from '../util/platform';
import { View, StyleSheet, Pressable, TextInput } from 'react-native'
import { Feather } from '@expo/vector-icons';
import { DateTime } from 'luxon';

import AppStyles from '../AppStyles';

import CustomText from './CustomText';
import Checkbox from './Checkbox';
import WebTooltip from './WebTooltip';
import { useTranslation } from '../i18n';

/**
 * TodoList - Component for managing todo items on reports
 *
 * @param {Array} todos - Array of todo objects
 * @param {Function} onAddTodo - Callback when adding a new todo (text) => void
 * @param {Function} onToggleTodo - Callback when toggling todo (todoId, completed) => void
 * @param {Function} onDeleteTodo - Callback when deleting todo (todoId) => void
 * @param {boolean} isAdmin - Whether the current user is an admin
 * @param {object} containerStyle - Optional style override for container
 */
const TodoList = ({ todos = [], onAddTodo, onToggleTodo, onDeleteTodo, isAdmin = false, containerStyle }) => {
  const { t } = useTranslation(['reports', 'common']);

  const [newTodoText, setNewTodoText] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  const handleAddTodo = async () => {
    if (!newTodoText.trim()) return;

    setIsAdding(true);
    try {
      await onAddTodo(newTodoText.trim());
      setNewTodoText('');
    } catch (error) {
      console.error('Error adding todo:', error);
    } finally {
      setIsAdding(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    return DateTime.fromMillis(timestamp).toLocaleString(DateTime.DATETIME_MED);
  };

  const renderTodoItem = (todo) => {
    const isCompleted = todo.completed;

    return (
      <View key={todo.id} style={styles.todoItem}>
        <View style={styles.todoRow}>
          {isAdmin && (
            <Checkbox
              selected={isCompleted}
              onChangeValue={(checked) => onToggleTodo(todo.id, checked)}
              label={`Toggle ${todo.text}`}
            />
          )}

          <CustomText
            font="regular"
            size={15}
            style={[
              styles.todoText,
              isCompleted && styles.todoTextCompleted,
            ]}
          >
            {todo.text}
          </CustomText>

          {isAdmin && (
            <WebTooltip label={t('todos.deleteLabel')}>
              <Pressable
                onPress={() => {
                  // Confirm before deleting
                  if (isWeb()) {
                    if (window.confirm(t('todos.deleteConfirm', { text: todo.text }))) {
                      onDeleteTodo(todo.id);
                    }
                  } else {
                    const { Alert } = require('react-native');
                    Alert.alert(
                      t('todos.deleteTitle'),
                      t('todos.deleteConfirm', { text: todo.text }),
                      [
                        { text: t('common:actions.cancel'), style: 'cancel' },
                        { text: t('common:actions.delete'), style: 'destructive', onPress: () => onDeleteTodo(todo.id) },
                      ],
                    );
                  }
                }}
                accessibilityRole="button"
                accessibilityLabel={t('todos.deleteLabel')}
                style={styles.deleteButton}
              >
                <Feather name="trash-2" size={16} color={AppStyles.colorSet.textMuted} />
              </Pressable>
            </WebTooltip>
          )}
        </View>
      </View>
    );
  };

  const pendingTodos = todos.filter((t) => !t.completed);
  const completedTodos = todos.filter((t) => t.completed);

  return (
    <View style={[styles.container, containerStyle]}>
      <View style={styles.header}>
        <CustomText font="semiBold" size={18} color={AppStyles.colorSet.textPrimary}>
          {t('todos.title')}
        </CustomText>
        <CustomText font="regular" size={14} color={AppStyles.colorSet.textSecondary}>
          {t('todos.remaining', { count: pendingTodos.length })}
        </CustomText>
      </View>

      {todos.length === 0 ? (
        <View style={styles.emptyState}>
          <Feather name="check-circle" size={40} color={AppStyles.colorSet.textMuted} />
          <CustomText
            font="medium"
            size={14}
            color={AppStyles.colorSet.textPrimary}
            style={{ marginTop: 8 }}
          >
            {t('todos.empty')}
          </CustomText>
          <CustomText
            font="regular"
            size={12}
            color={AppStyles.colorSet.textMuted}
            style={{ marginTop: 4, textAlign: 'center', maxWidth: 280 }}
          >
            {t('todos.emptyHint')}
          </CustomText>
        </View>
      ) : (
        <>
          {pendingTodos.length > 0 && (
            <View style={styles.todoList}>
              {pendingTodos.map((todo) => renderTodoItem(todo))}
            </View>
          )}

          {completedTodos.length > 0 && (
            <View style={styles.completedSection}>
              <View style={styles.todoList}>
                {completedTodos.map((todo) => renderTodoItem(todo))}
              </View>
            </View>
          )}
        </>
      )}

      {isAdmin && (
        <View style={styles.addTodoSection}>
          <TextInput
            style={styles.input}
            placeholder={t('todos.placeholder')}
            placeholderTextColor={AppStyles.colorSet.textMuted}
            value={newTodoText}
            onChangeText={setNewTodoText}
            onSubmitEditing={handleAddTodo}
            returnKeyType="done"
            editable={!isAdding}
          />
          <Pressable
            onPress={handleAddTodo}
            disabled={isAdding || !newTodoText.trim()}
            style={({ hovered, focused, pressed }) => {
              const isDisabled = isAdding || !newTodoText.trim();
              return [
                styles.addButton,
                !isDisabled && { cursor: 'pointer' },
                !isDisabled && (hovered || focused) && styles.addButtonHovered,
                !isDisabled && pressed && { opacity: 0.85 },
                isDisabled && styles.addButtonDisabled,
              ];
            }}
            accessibilityRole="button"
            accessibilityLabel={t('todos.addLabel')}
          >
            <Feather
              name={isAdding ? 'loader' : 'plus'}
              size={18}
              color={(isAdding || !newTodoText.trim()) ? AppStyles.colorSet.textMuted : '#000000'}
            />
            <CustomText
              font="medium"
              size={14}
              color={(isAdding || !newTodoText.trim()) ? AppStyles.colorSet.textMuted : '#000000'}
              style={{ marginLeft: 6 }}
            >
              {isAdding ? t('common:actions.adding') : t('common:actions.add')}
            </CustomText>
          </Pressable>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginVertical: AppStyles.spacing.md,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: AppStyles.spacing.sm,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: AppStyles.spacing.xl,
    backgroundColor: AppStyles.colorSet.inputBackground,
    borderRadius: AppStyles.borderRadii.md,
  },
  emptyText: {
    marginTop: AppStyles.spacing.sm,
  },
  todoList: {
    gap: AppStyles.spacing.sm,
  },
  todoItem: {
    backgroundColor: AppStyles.colorSet.cardBackground,
    borderWidth: 1,
    borderColor: AppStyles.colorSet.cardBorder,
    borderRadius: AppStyles.borderRadii.md,
    padding: AppStyles.spacing.md,
  },
  todoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: AppStyles.spacing.sm,
  },
  todoContent: {
    flex: 1,
  },
  todoText: {
    flex: 1,
  },
  todoTextCompleted: {
    textDecorationLine: 'line-through',
    color: AppStyles.colorSet.textMuted,
  },
  todoMeta: {
    flexDirection: isWeb() ? 'row' : 'column',
    gap: isWeb() ? AppStyles.spacing.sm : 2,
  },
  todoMetaSeparator: {
    marginLeft: isWeb() ? AppStyles.spacing.sm : 0,
  },
  deleteButton: {
    padding: AppStyles.spacing.xs,
  },
  addTodoSection: {
    flexDirection: 'row',
    gap: AppStyles.spacing.sm,
    marginTop: AppStyles.spacing.md,
  },
  input: {
    flex: 1,
    // Allow the input to shrink in the flex row on web (inputs default to
    // min-width:auto, which otherwise pushes the Add button off the card).
    minWidth: 0,
    backgroundColor: AppStyles.colorSet.inputBackground,
    borderWidth: 1,
    borderColor: AppStyles.colorSet.inputBorder,
    borderRadius: AppStyles.borderRadii.md,
    paddingHorizontal: AppStyles.spacing.md,
    paddingVertical: AppStyles.spacing.sm,
    fontSize: 15,
    fontFamily: 'Poppins-Regular',
    color: AppStyles.colorSet.textPrimary,
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: AppStyles.spacing.md,
    paddingVertical: AppStyles.spacing.sm,
    borderRadius: AppStyles.borderRadii.md,
    backgroundColor: AppStyles.colorSet.brandPrimary,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 1,
  },
  addButtonDisabled: {
    backgroundColor: AppStyles.colorSet.inputBackground,
    shadowOpacity: 0,
    elevation: 0,
  },
  // Web hover/focus feedback — lift on the filled Add button
  addButtonHovered: {
    opacity: 0.92,
    shadowOpacity: 0.2,
    shadowRadius: 6,
    elevation: 2,
  },
  completedSection: {
    marginTop: AppStyles.spacing.lg,
  },
});

export default TodoList;
